import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { theme } from './theme';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/common/ProtectedRoute';
import LoginPage from './pages/auth/LoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AuditLogsPage from './pages/admin/AuditLogsPage';
import SubscriptionsPage from './pages/admin/SubscriptionsPage';
import AssistantDashboard from './pages/assistant/AssistantDashboard';
import DeviceAssignment from './pages/assistant/DeviceAssignment';
import QuickAssign from './pages/assistant/QuickAssign';

import ClientsPage from './pages/clients/ClientsPage';
import CreateClientPage from './pages/clients/CreateClientPage';
import ClientDetailPage from './pages/clients/ClientDetailPage';
import EditClientPage from './pages/clients/EditClientPage';
import DevicesPage from './pages/devices/DevicesPage';
import DeviceDetailPage from './pages/devices/DeviceDetailPage';
import SimCardsPage from './pages/simcards/SimCardsPage';
import RenewalsPage from './pages/renewals/RenewalsPage';
import CalendarPage from './pages/calendar/CalendarPage';
import LiveMapPage from './pages/map/LiveMapPage';

// Device Revisions/Maintenance
import DeviceRevisionsPage from './pages/devices/revisions/DeviceRevisionsPage';
import DeviceRevisionHistory from './pages/devices/revisions/DeviceRevisionHistory';
import CreateDeviceRevision from './pages/devices/revisions/CreateDeviceRevision';

// Device Groups
import DeviceGroups from './pages/devices/groups/DeviceGroups';

// Invoices
import InvoiceGeneratorPage from './pages/invoices/InvoiceGeneratorPage';
import MonitoringPage from './pages/monitoring/MonitoringPage';

// Reports
import FinancialReportPage from './pages/reports/FinancialReportPage';

// Bulk Operations
import BulkOperationsPage from './pages/bulk/BulkOperationsPage';

// SIM Card Detail
import SimCardDetailPage from './pages/simcards/SimCardDetailPage';

// Technician Management
import TechnicianManagement from './components/TechnicianManagement';
import TechnicianReports from './components/TechnicianReports';

// Technician Finance
import TechnicianFinancePage from './pages/technicians/TechnicianFinancePage';
import TechnicianBalanceDetail from './pages/technicians/TechnicianBalanceDetail';
import ExpensesPage from './pages/finance/ExpensesPage';

// Technician Portal
import TechnicianDashboard from './pages/technician/TechnicianDashboard';
import TechnicianMyFinances from './pages/technician/TechnicianMyFinances';
import TechnicianCalendar from './pages/technician/TechnicianCalendar';
import TechnicianClients from './pages/technician/TechnicianClients';

// Bot / CRM
import LeadsCRMPage from './pages/admin/LeadsCRMPage';
import LeadsBoardPage from './pages/admin/LeadsBoardPage';
import RoutesManagementPage from './pages/admin/RoutesManagementPage';
import KnowledgeBasePage from './pages/admin/KnowledgeBasePage';
import BotInsightsPage from './pages/admin/BotInsightsPage';

// Hubs (pantallas con pestañas que unifican secciones relacionadas)
import FinanzasHub from './pages/hubs/FinanzasHub';
import RedesSocialesHub from './pages/hubs/RedesSocialesHub';
import InventarioHub from './pages/hubs/InventarioHub';
import ClientesHub from './pages/hubs/ClientesHub';

const AdminPage = () => <div>Administración Page - En desarrollo</div>;
const UnauthorizedPage = () => <div>No autorizado</div>;

const DashboardRedirect: React.FC = () => {
  const { isAdmin, isTechnician, user } = useAuth();
  const redirectTo = isTechnician ? '/technician/dashboard' : isAdmin ? '/admin/dashboard' : '/assistant/dashboard';

  return <Navigate to={redirectTo} replace />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />

            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              {/* Dashboard redirect */}
              <Route index element={<DashboardRedirect />} />

              {/* Admin dashboard */}
              <Route
                path="admin/dashboard"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Admin audit & subscriptions */}
              <Route
                path="admin/audit"
                element={
                  <ProtectedRoute adminOnly>
                    <AuditLogsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/subscriptions"
                element={
                  <ProtectedRoute adminOnly>
                    <SubscriptionsPage />
                  </ProtectedRoute>
                }
              />

              {/* Assistant dashboard */}
              <Route
                path="assistant/dashboard"
                element={
                  <ProtectedRoute>
                    <AssistantDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Assistant device assignment */}
              <Route
                path="assistant/devices"
                element={
                  <ProtectedRoute>
                    <DeviceAssignment />
                  </ProtectedRoute>
                }
              />

              {/* Assistant quick assign */}
              <Route
                path="assistant/quick-assign"
                element={
                  <ProtectedRoute>
                    <QuickAssign />
                  </ProtectedRoute>
                }
              />

              {/* Technician portal routes */}
              <Route
                path="technician/dashboard"
                element={
                  <ProtectedRoute technicianOnly>
                    <TechnicianDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="technician/calendar"
                element={
                  <ProtectedRoute technicianOnly>
                    <TechnicianCalendar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="technician/clients"
                element={
                  <ProtectedRoute technicianOnly>
                    <TechnicianClients />
                  </ProtectedRoute>
                }
              />
              <Route
                path="technician/finances"
                element={
                  <ProtectedRoute technicianOnly>
                    <TechnicianMyFinances />
                  </ProtectedRoute>
                }
              />

              {/* Bot / CRM routes (admin only) */}
              <Route
                path="bot/hub"
                element={<Navigate to="/bot/leads" replace />}
              />
              <Route
                path="bot/leads"
                element={
                  <ProtectedRoute adminOnly>
                    <LeadsCRMPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="bot/leads-board"
                element={
                  <ProtectedRoute adminOnly>
                    <LeadsBoardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="bot/routes"
                element={
                  <ProtectedRoute adminOnly>
                    <RoutesManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="bot/knowledge"
                element={
                  <ProtectedRoute adminOnly>
                    <KnowledgeBasePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="bot/insights"
                element={
                  <ProtectedRoute adminOnly>
                    <BotInsightsPage />
                  </ProtectedRoute>
                }
              />

              {/* Shared routes */}
              {/* Hubs con pestañas (unifican secciones; las rutas individuales siguen vivas) */}
              <Route path="clientes" element={<ClientesHub />} />
              <Route path="inventario" element={<InventarioHub />} />
              <Route
                path="finanzas"
                element={
                  <ProtectedRoute adminOnly>
                    <FinanzasHub />
                  </ProtectedRoute>
                }
              />
              <Route
                path="redes"
                element={
                  <ProtectedRoute adminOnly>
                    <RedesSocialesHub />
                  </ProtectedRoute>
                }
              />
              <Route path="clients" element={<ClientsPage />} />
              <Route path="clients/new" element={<CreateClientPage />} />
              <Route path="clients/:clientId" element={<ClientDetailPage />} />
              <Route path="clients/:clientId/edit" element={<EditClientPage />} />
              <Route path="devices" element={<DevicesPage />} />
              <Route path="devices/:deviceId" element={<DeviceDetailPage />} />

              {/* Device Revisions/Maintenance */}
              <Route path="devices/revisions" element={<DeviceRevisionsPage />} />
              <Route path="devices/:deviceId/revision-history" element={<DeviceRevisionHistory />} />
              <Route path="devices/:deviceId/create-revision" element={<CreateDeviceRevision />} />

              <Route path="simcards" element={<SimCardsPage />} />
              <Route path="simcards/:id" element={<SimCardDetailPage />} />
              <Route path="renewals" element={<RenewalsPage />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="live-map" element={<LiveMapPage />} />
              <Route path="invoices" element={<InvoiceGeneratorPage />} />
              <Route path="monitoreo" element={<MonitoringPage />} />
              <Route path="bulk-operations" element={<BulkOperationsPage />} />

              {/* Device Groups */}
              <Route path="devices/groups" element={<DeviceGroups />} />
              <Route path="devices/groups/:groupType" element={<DeviceGroups />} />

              {/* Technician Management Routes */}
              <Route
                path="technicians"
                element={
                  <ProtectedRoute adminOnly>
                    <TechnicianManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="technicians/reports"
                element={
                  <ProtectedRoute adminOnly>
                    <TechnicianReports />
                  </ProtectedRoute>
                }
              />

              {/* Technician Finance Routes */}
              <Route
                path="technicians/finance"
                element={
                  <ProtectedRoute adminOnly>
                    <TechnicianFinancePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="technicians/:id/balance"
                element={
                  <ProtectedRoute adminOnly>
                    <TechnicianBalanceDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="expenses"
                element={
                  <ProtectedRoute adminOnly>
                    <ExpensesPage />
                  </ProtectedRoute>
                }
              />

              {/* Admin-only routes */}
              <Route
                path="admin/*"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="reports"
                element={
                  <ProtectedRoute adminOnly>
                    <FinancialReportPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
